import React, { useEffect } from 'react';
import config from '../config';
import { navigation_load_menu, navigation_load_geo_list } from '../redux/reducers/navigationSlice';
import { auth_load } from '../redux/reducers/authSlice';
import { wrapper } from '../redux/store';

//Components
import Layout from '../components/Layout';
import { FORECAST_HOME_DESCRIPTION, FORECAST_HOME_PAGE_TITLE } from '../consts/seo-copy';
import NotFound from '../components/NotFound';
import HomeContainer from '../components/Home/HomeContainer';
import { useWindowSize } from '../helpers/hooks/useWindowSize';
import { AD_SLOT_TYPE__BOTTOM, AD_SLOT_TYPE__TOP, IAdSlotConfig } from '@emarketer/googleadmanager';
import { IRightRailEntry, fetchAssetsForBranding } from '@emarketer/componentlibrary';
import logger from '../logger/logger';
import { getDataLayerPropsForIndexPage } from '../helpers/DataLayerHelper';
import { getSvcDependenciesFromProcessEnv, IServiceDebugInfo, isShowServiceDebugInfo } from '@emarketer/debuginfo';
import { trimTrailingSlash } from '@emarketer/code-helpers';
import { useUser } from '@emarketer/auth';

const brandingProps = {
  isTransparent: false,
  enablePodRedirection: true,
};

interface HomeProps {
  brandingAssets: IRightRailEntry[];
  serviceDependenciesDebugInfo: IServiceDebugInfo[];
  statusCode: number;
}

const Home = ({ brandingAssets, serviceDependenciesDebugInfo, statusCode }: HomeProps) => {
  useEffect(() => {
    if (typeof window !== 'undefined' && window.iiAna) {
      window.iiAna('page');
    }
  }, []);

  // ------------------------- MAKE SURE TO CHANGE VALUES FOR SEO -------------------------
  // SEO
  const pageTitle = FORECAST_HOME_PAGE_TITLE;
  const description = FORECAST_HOME_DESCRIPTION;
  const isLoggedIn = false;
  const isMobile = useWindowSize().isMobile;

  // ERROR PAGE
  if (statusCode > 200) {
    return <NotFound statusCode={statusCode} />;
  }
  const adSlotsConfig: IAdSlotConfig[] = [
    {
      adSlotType: AD_SLOT_TYPE__TOP,
    },
    {
      adSlotType: AD_SLOT_TYPE__BOTTOM,
    },
  ];

  const { user } = useUser();

  return (
    <Layout
      title={pageTitle}
      description={description}
      width={'full'}
      brandingProps={brandingProps}
      canonicalUrl={trimTrailingSlash(config.publicPath)}
      adSlotsConfig={adSlotsConfig}
      brandingAssets={brandingAssets}
      dataLayerProps={getDataLayerPropsForIndexPage(user.subscriptionTier)}
      serviceDependenciesDebugInfo={serviceDependenciesDebugInfo}
    >
      <HomeContainer isLoggedIn={isLoggedIn} isMobile={isMobile} />
    </Layout>
  );
};

export const getServerSideProps = wrapper.getServerSideProps((store) => async (context): Promise<any> => {
  await store.dispatch(navigation_load_menu(config.defaultGeoLocationID));
  await store.dispatch(navigation_load_geo_list());
  // Parse auth token server-side so @emarketer/auth/server never runs in the client bundle
  await store.dispatch(auth_load(context.req.headers.cookie ?? ''));

  const url = process.env.COMMON_DYNAMIC_CONTENT_WEBAPI!;
  const user = process.env.COMMON_DYNAMIC_CONTENT_USER!;
  const password = process.env.COMMON_DYNAMIC_CONTENT_PASSWORD!;
  const brandingAssets = await fetchAssetsForBranding(url, user, password, fetch, logger);
  const statusCode = context.res.statusCode;

  //#region DEBUG-INFO
  // Expose service & components version/debug info if non-prod environment. Because we want to show debug info of current service plus its key dependencies (e.g. Component Library), we need to pull "dependencies info" from PROCESS.ENV.XXXX, which is only available SERVER-SIDE. So, we "read" those here and then pass that array of dependencies to render client-side. Read more in "DebugInfo" readme/wiki
  const serviceDependenciesDebugInfo = isShowServiceDebugInfo(config.currentEnvironment, context.req)
    ? getSvcDependenciesFromProcessEnv(process.env)
    : [];
  //#endregion DEBUG-INFO

  return {
    props: {
      brandingAssets,
      serviceDependenciesDebugInfo,

      statusCode,
    },
  };
});

export default Home;
